import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject } from '@angular/core';
import { MinibasketInitiatorService } from '@df/basket/minibasket/minibasket-initiator.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { AbModule } from '@df/module-ab/ab.module';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Subscription } from 'rxjs';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../../ui/modal/modal-layout-right.component';
import { BasketFreeDeliveryNoticeComponent } from '../free-shipping/basket-free-shipping.component';
import { BasketViewItemComponent } from '../view-item/basket-view-item.component';
import { BasketViewPayWithComponent } from '../view-paywith/basket-view-pay-with.component';
import { BasketViewSubmitComponent } from '../view-submit/basket-view-submit.component';
import { BasketViewTotalsComponent } from '../view-totals/basket-view-totals.component';
import { NBasketViewCms } from '../view/basket-view-cms.component';
export namespace NCmsMinibasket {
    export const BLOCK_ID = 'minibasket';
    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}
    export interface IBlockDataContent {
        footerNote: string;
        bottomComponents?: NICmsContentComponent.IData[];
        emptyComponents: NICmsContentComponent.IData[];
        payment_method_icons?: NBasketViewCms.IPaymentMethodIcon[];
    }
}
@DfCmsModal({
    id: NCmsMinibasket.BLOCK_ID,
    name: 'Minibasket',
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: NModalLayoutRight.getWidth(sizeName)
    }),
    initiators: [MinibasketInitiatorService],
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: ['X', 'L'],
    prerenderAllowed: false,
    forbiddenLocation: /^\/(basket|checkout)$/
})
@ICmsComponent<MinibasketComponent>({
    id: NCmsMinibasket.BLOCK_ID,
    block: {
        url: NCmsMinibasket.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Minibasket',
    description: 'Minibasket Content',
    fields: [
        <NICmsContentField.IField>{
            key: 'footerNote',
            type: NICmsContentField.EFieldType.text,
            title: 'Footer note'
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom basket components',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IComponentsField>{
            key: 'emptyComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Empty basket components',
            itemName: 'component',
            default: []
        },
        NBasketViewCms.paymentMethodIconsIcmsField
    ]
})
@Component({
    selector: 'minibasket',
    templateUrl: './minibasket.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        TranslationModule,
        ModalLayoutRightComponent,
        BasketViewPayWithComponent,
        BasketViewSubmitComponent,
        forwardRef(()=>BasketViewTotalsComponent),
        ICmsClientModule,
        BasketViewItemComponent,
        BasketFreeDeliveryNoticeComponent,
        AbModule,
        CommonModule,
        OrderByPipe
    ]
})
export class MinibasketComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NCmsMinibasket.IBlockDataContent;

    protected override subscriptions: Subscription = new Subscription();
    app = inject(App);

    /**
     * Basket shorthand
     */
    get basket(): Basket {
        return this.app.customer.basket;
    }

    isSalable?: boolean;

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdate()));
        this.onBasketUpdate();
    }

    private onBasketUpdate() {
        this.isSalable = this.basket.isSalable;
        if (this.initiated) {
            this.detectChanges();
        }
    }

    itemTrackFn = (index: number, item: BasketItem) => item.id;
}
